

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewOrderItineraryModal = ({
    isOpen,
    onClose,
    onSave,
    travelOrderId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        travel_order_id: travelOrderId || '',
        day_line_item_id: '',
        trip_component_id: '',
        order_item_id: '',
        order_pax_id: '',
        travefy_sync: false
    });

    const [formErrors, setFormErrors] = useState({});
    const [travelOrders, setTravelOrders] = useState([]);
    const [dayLineItems, setDayLineItems] = useState([]);
    const [tripComponents, setTripComponents] = useState([]);
    const [orderItems, setOrderItems] = useState([]);
    const [orderPax, setOrderPax] = useState([]);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [loadingDayLineItems, setLoadingDayLineItems] = useState(false);
    const [loadingComponents, setLoadingComponents] = useState(false);
    const [loadingOrderItems, setLoadingOrderItems] = useState(false);
    const [loadingOrderPax, setLoadingOrderPax] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            if (!travelOrderId) {
                fetchTravelOrders();
            }
            fetchDayLineItems();
            fetchTripComponents();
        }
    }, [isOpen, travelOrderId]);

    useEffect(() => {
        if (formData.travel_order_id) {
            fetchOrderItems(formData.travel_order_id);
            fetchOrderPax(formData.travel_order_id);
        }
    }, [formData.travel_order_id]);

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const fetchDayLineItems = async () => {
        setLoadingDayLineItems(true);
        try {
            const response = await axiosInstance.get('/day-line-items/');
            setDayLineItems(response.data || []);
        } catch (error) {
            console.error('Error fetching day line items:', error);
        } finally {
            setLoadingDayLineItems(false);
        }
    };

    const fetchTripComponents = async () => {
        setLoadingComponents(true);
        try {
            const response = await axiosInstance.get('/trip-components/');
            setTripComponents(response.data || []);
        } catch (error) {
            console.error('Error fetching Trip Logistics:', error);
        } finally {
            setLoadingComponents(false);
        }
    };

    const fetchOrderItems = async (orderId) => {
        setLoadingOrderItems(true);
        try {
            const response = await axiosInstance.get(`/travel-order-items/?travel_order_id=${orderId}`);
            setOrderItems(response.data || []);
        } catch (error) {
            console.error('Error fetching order items:', error);
        } finally {
            setLoadingOrderItems(false);
        }
    };

    const fetchOrderPax = async (orderId) => {
        setLoadingOrderPax(true);
        try {
            const response = await axiosInstance.get(`/travel-order-pax/?travel_order_id=${orderId}`);
            setOrderPax(response.data || []);
        } catch (error) {
            console.error('Error fetching order pax:', error);
        } finally {
            setLoadingOrderPax(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Itinerary Name is required';
        if (!formData.travel_order_id) errors.travel_order_id = 'Travel Order is required';
        if (!formData.trip_component_id) errors.trip_component_id = 'Trip Logistics is required';
        if (!formData.order_item_id) errors.order_item_id = 'Order Item is required';
        if (!formData.order_pax_id) errors.order_pax_id = 'Order Pax is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                travel_order_id: parseInt(formData.travel_order_id),
                day_line_item_id: formData.day_line_item_id ? parseInt(formData.day_line_item_id) : null,
                trip_component_id: parseInt(formData.trip_component_id),
                order_item_id: parseInt(formData.order_item_id),
                order_pax_id: parseInt(formData.order_pax_id),
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                travel_order_id: travelOrderId || '',
                day_line_item_id: '',
                trip_component_id: '',
                order_item_id: '',
                order_pax_id: '',
                travefy_sync: false
            });
        } catch (error) {
            console.error('Error saving Order Itinerary:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Order Itinerary',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Order Itinerary</MDTypography>
                        <IconButton onClick={onClose} variant="outlined" margin="normal" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Itinerary Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                {!travelOrderId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.travel_order_id} required>
                                            <InputLabel>Travel Order</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Travel Order"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select a Travel Order</em></MenuItem>
                                                {travelOrders.map(to => (
                                                    <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.travel_order_id && <MDTypography variant="caption" color="error">{formErrors.travel_order_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_component_id} required>
                                        <InputLabel>Trip Logistics</InputLabel>
                                        <Select
                                            name="trip_component_id"
                                            value={formData.trip_component_id}
                                            onChange={handleInputChange}
                                            label="Trip Logistics"

                                            disabled={loadingComponents}
                                        >
                                            <MenuItem value=""><em>Select a Trip Logistics</em></MenuItem>
                                            {tripComponents.map(tc => (
                                                <MenuItem key={tc.id} value={tc.id}>{tc.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.trip_component_id && <MDTypography variant="caption" color="error">{formErrors.trip_component_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Day Line Item</InputLabel>
                                        <Select
                                            name="day_line_item_id"
                                            value={formData.day_line_item_id}
                                            onChange={handleInputChange}
                                            label="Day Line Item"

                                            disabled={loadingDayLineItems}
                                        >
                                            <MenuItem value=""><em>Select a Day Line Item</em></MenuItem>
                                            {dayLineItems.map(dli => (
                                                <MenuItem key={dli.id} value={dli.id}>{dli.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.order_item_id} required>
                                        <InputLabel>Order Item</InputLabel>
                                        <Select
                                            name="order_item_id"
                                            value={formData.order_item_id}
                                            onChange={handleInputChange}
                                            label="Order Item"

                                            disabled={loadingOrderItems || !formData.travel_order_id}
                                        >
                                            <MenuItem value=""><em>Select an Order Item</em></MenuItem>
                                            {orderItems.map(oi => (
                                                <MenuItem key={oi.id} value={oi.id}>{oi.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.order_item_id && <MDTypography variant="caption" color="error">{formErrors.order_item_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.order_pax_id} required>
                                        <InputLabel>Order Pax</InputLabel>
                                        <Select
                                            name="order_pax_id"
                                            value={formData.order_pax_id}
                                            onChange={handleInputChange}
                                            label="Order Pax"

                                            disabled={loadingOrderPax || !formData.travel_order_id}
                                        >
                                            <MenuItem value=""><em>Select an Order Passenger</em></MenuItem>
                                            {orderPax.map(op => (
                                                <MenuItem key={op.id} value={op.id}>{op.passenger_name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.order_pax_id && <MDTypography variant="caption" color="error">{formErrors.order_pax_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.travefy_sync} name="travefy_sync" onChange={handleInputChange} />}
                                        label="Synced with Travefy"
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Order Itinerary'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewOrderItineraryModal;

